// import axios from 'axios';
import { useEffect, useState, FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Accordion from '@radix-ui/react-accordion';

import { text } from '../text';
// import { URLS } from '../config';
import { utils } from '../utils';
import { svg } from '../assets/svg';
import { formatMoney, theme } from '../constants';
import { components } from '../components';
import { Order } from '../types/OrderType';
import { fetchOrders } from '../services/orderService';
import FloatingWhatsAppButton from '../components/FloatingWhatsAppButton';

export const OrderHistory: FC = () => {
  const location = useLocation();
  const { orderId } = location.state || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ordersData, setOrdersData] = useState<Order[]>([]);
  const [expandedOrder, setExpandedOrder] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const getOrders = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await fetchOrders(10, page);

      // Set the array of orders and total pages
      setOrdersData(response.data.data);
      setTotalPages(response.data.totalPages);
      // If orderId exists in location.state, find and expand it
      if (
        orderId &&
        response.data.data.some((order: any) => order.id === orderId)
      ) {
        setExpandedOrder(String(orderId));
      }
    } catch (error) {
//      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOrder = (orderId: number) => {
    setExpandedOrder(String(orderId));
  };

  useEffect(() => {
    if (orderId) {
      handleOpenOrder(orderId);
    }
  }, [orderId]);

  useEffect(() => {
    getOrders();
  }, [page]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(); // Converts to human-readable format
  };

  const getStatusColor = (status: string) => {
    if (status === 'workplace_approved') return '#51BA74'; // Green
    if (status === 'pending') return '#F5C102'; // Orange
    return status === 'delivered'
      ? '#51BA74'
      : status === 'shipping'
        ? '#F5C102'
        : '#FF4343';
  };

  const renderHeader = () => {
    return <components.Header goBack={false} basket={true} />;
  };

  const renderFooter = (): JSX.Element => {
    if (loading) return <components.TabLoader />;

    return (
      <components.Footer
        logo={true}
        copyright={true}
        links={[
          { label: 'Home', path: '/Home' },
          { label: 'About', path: '/About' },
          { label: 'Services', path: '/Services' },
          { label: 'Contact', path: '/Contact' },
        ]}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    if (loading) return <components.Loader />;

    return (
      <div style={{ maxWidth: '1280px', margin: '0 auto', flex: 1 }}>
        <main style={{ padding: '10px 0 20px 0', marginTop: 180 }}>
          {ordersData.length === 0 ? (
            <div style={{ textAlign: 'center', marginTop: '50px', fontSize: '18px', color: theme.colors.textColor }}>
              No orders available.
            </div>
          ) : (
            <Accordion.Root
              type='single'
              value={expandedOrder || undefined}
              onValueChange={setExpandedOrder}
              collapsible={true}
            >
              {ordersData.map((order, index) => (
                <Accordion.Item key={index} value={order.id.toString()}>
                  <Accordion.Trigger
                    style={{
                      flexDirection: 'column',
                      width: '100%',
                      display: 'flex',
                      padding: '20px 20px 14px 20px',
                      borderTop: '1px solid #E7EBEB',
                      marginTop: 20,
                    }}
                  >
                    <div style={{ width: '100%', marginBottom: 10, ...utils.rowCenterSpcBtween() }}>
                      <text.H5 style={{ color: theme.colors.mainColor }}>#{order.order_number}</text.H5>
                      <text.H5 style={{ color: theme.colors.mainColor }}>ETB&nbsp; {formatMoney(order.total_price)}</text.H5>
                    </div>
                    <div style={{ width: '100%', ...utils.rowCenterSpcBtween() }}>
                      <text.T12 style={{ textTransform: 'capitalize', color: getStatusColor(order.status) }}>
                        {order.status}
                      </text.T12>
                      <text.T12 style={{ color: theme.colors.textColor }}>{formatDate(order.created_at)}</text.T12>
                    </div>
                  </Accordion.Trigger>
                  <Accordion.Content style={{ padding: '10px 20px 20px 20px' }}>
                    <div style={{ backgroundColor: '#FAFAFA', padding: 20, borderRadius: 15, marginBottom: 20 }}>
                      {order.products.map((item: any, index: any, array: any) => {
                        const isLast = index === array.length - 1;
                        return (
                          <div key={index} style={{ width: '100%', ...utils.rowCenterSpcBtween(), marginBottom: isLast ? 10 : 6 }}>
                            <text.T14 style={{ color: theme.colors.textColor }}>{item.product.name}</text.T14>
                            <text.T14 style={{ color: theme.colors.textColor }}>{item.quantity} x {formatMoney(item.price)}</text.T14>
                          </div>
                        );
                      })}
                      <div style={{ height: 1, width: '100%', backgroundColor: '#E7EBEB', marginBottom: 10 }} />
                      <div style={{ ...utils.rowCenterSpcBtween() }}>
                        <text.T14 style={{ color: theme.colors.mainColor }}>Total</text.T14>
                        <text.T14 style={{ color: theme.colors.mainColor }}>ETB&nbsp; {formatMoney(order.total_price)}</text.T14>
                      </div>
                    </div>
                    <div style={{ width: '100%', ...utils.rowCenterSpcBtween() }}>
                      <button style={{ lineHeight: 0 }}>
                        <svg.RepeatOrderSvg />
                      </button>
                      <button style={{ lineHeight: 0 }} onClick={() => navigate('/LeaveAReview')}>
                        <svg.LeaveAReviewSvg />
                      </button>
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          )}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
            {page > 1 && <button onClick={() => setPage((prev) => prev - 1)}>Previous</button>}
            {page < totalPages && <button onClick={() => setPage((prev) => prev + 1)}>Next</button>}
          </div>
        </main>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {renderHeader()}
        <div style={{ flex: 1 }}>{renderContent()}</div>
        {renderFooter()}
        <FloatingWhatsAppButton />

      </div>
    </>
  );
};
